/**
 * Ribbon variants — audience-aware copy for the landing-page trial ribbon.
 *
 * Two audiences:
 *   • `buyer`  → shopping-flavored nudges (SibaScore, AI Cart™, Sancta).
 *     Guest shoppers never see the word "trial" or "signup" — that copy
 *     misleads them into thinking SociBaba is a SaaS they must pay for.
 *   • `seller` → the legacy 14-day-trial pitch for brands and creators
 *     who arrived from /pricing or /for-brands.
 *
 * The active audience is inferred from the visitor's recent route journey
 * (see `useVisitorIntent`) and defaults to whatever the admin has set as
 * the site default (usually `buyer`). Variants are also editable live via
 * the super-admin console (`/api/admin/marketing/ribbon-config`).
 *
 * Analytics fanout is unchanged — `trackRibbon` still gates on cookie
 * consent so we don't leak events pre-consent.
 */
import { track, getConsent } from "@/lib/analytics";

export type RibbonAudience = "buyer" | "seller";

export interface RibbonVariant {
  id: string;
  body: string;
  emphasis: string;
  cta: string;
  href: string;
}

export interface RibbonConfig {
  enabled: boolean;
  default_audience: RibbonAudience;
  buyer_variants: RibbonVariant[];
  seller_variants: RibbonVariant[];
}

// ── Shipped defaults ────────────────────────────────────────────────────
// Must stay in sync with backend `services/marketing_ribbon_config.py`. The
// frontend uses these when the admin config endpoint is offline or slow so
// the ribbon never renders empty on first paint.
export const DEFAULT_BUYER_VARIANTS: RibbonVariant[] = [
  {
    id: "buyer-A",
    body: "Not sure what to buy?",
    emphasis: "Get one honest score out of 1000 — free forever.",
    cta: "Try SibaScore™ Search",
    href: "/shop",
  },
  {
    id: "buyer-B",
    body: "Curious how AI Cart™ works?",
    emphasis: "Paste any marketplace link, find a better price.",
    cta: "Open AI Cart™",
    href: "/ai-cart",
  },
  {
    id: "buyer-C",
    body: "Talk to Sancta in 12 languages.",
    emphasis: "Ask anything — no signup, no card, just answers.",
    cta: "Chat with Sancta",
    href: "/sancta",
  },
];

export const DEFAULT_SELLER_VARIANTS: RibbonVariant[] = [
  {
    id: "seller-A",
    body: "Still exploring?",
    emphasis: "14-day trial ready when you are.",
    cta: "Start free trial",
    href: "/signup",
  },
  {
    id: "seller-B",
    body: "Ready to turn comments into revenue?",
    emphasis: "Try SociBaba free for 14 days.",
    cta: "Start my trial",
    href: "/signup",
  },
  {
    id: "seller-C",
    body: "Your first 14 days are on us.",
    emphasis: "See what SociBaba can do for your brand.",
    cta: "Claim my trial",
    href: "/signup",
  },
];

export const DEFAULT_RIBBON_CONFIG: RibbonConfig = {
  enabled: true,
  default_audience: "buyer",
  buyer_variants: DEFAULT_BUYER_VARIANTS,
  seller_variants: DEFAULT_SELLER_VARIANTS,
};

// ── Storage keys ────────────────────────────────────────────────────────
const VARIANT_KEY = "sb_trial_ribbon_variant";          // last-picked variant id
const AUDIENCE_MANUAL_KEY = "sb_ribbon_audience_manual";// user-flipped mode
const VISITOR_INTENT_KEY = "sb_visitor_intent";         // route-inferred intent

/**
 * Decide the active audience for the ribbon.
 *
 * Priority order:
 *   1. Manual override — the user clicked "Just shopping?" / "Are you a brand?"
 *   2. Route-inferred intent from `useVisitorIntent` hook (localStorage)
 *   3. Admin-set default (config.default_audience, usually "buyer")
 */
export function detectAudience(
  configDefault: RibbonAudience = "buyer",
): RibbonAudience {
  try {
    const manual = localStorage.getItem(AUDIENCE_MANUAL_KEY);
    if (manual === "buyer" || manual === "seller") return manual;
    const inferred = localStorage.getItem(VISITOR_INTENT_KEY);
    if (inferred === "buyer" || inferred === "seller") return inferred;
  } catch {
    /* private mode / SSR */
  }
  return configDefault;
}

/** Persist the user's manual audience choice. Setting `null` clears it so
 *  the next resolution falls back to auto-detect. */
export function setManualAudience(audience: RibbonAudience | null): void {
  try {
    if (audience === null) localStorage.removeItem(AUDIENCE_MANUAL_KEY);
    else localStorage.setItem(AUDIENCE_MANUAL_KEY, audience);
  } catch { /* ignore */ }
}

/**
 * Assign a stable variant to this browser within the given audience pool.
 * First call picks uniformly at random; subsequent calls return the same
 * choice for consistency. When the audience changes we re-pick because the
 * previous choice may not exist in the new pool.
 */
export function pickRibbonVariant(
  audience: RibbonAudience,
  pool: RibbonVariant[],
): RibbonVariant {
  if (pool.length === 0) {
    // Should never happen — backend guarantees a non-empty pool — but keep
    // a graceful fallback so the ribbon still renders.
    return DEFAULT_BUYER_VARIANTS[0];
  }
  const key = `${VARIANT_KEY}:${audience}`;
  try {
    const cached = localStorage.getItem(key);
    const found = pool.find((v) => v.id === cached);
    if (found) return found;
  } catch { /* private mode */ }
  const idx = Math.floor(Math.random() * pool.length);
  const chosen = pool[idx];
  try { localStorage.setItem(key, chosen.id); } catch { /* ignore */ }
  return chosen;
}

/** GA4 event fanout — only when the visitor has granted analytics consent. */
export function trackRibbon(
  event: string,
  variantId: string,
  surface: "landing" | "pricing",
  audience?: RibbonAudience,
) {
  if (getConsent() !== "granted") return;
  track(event, { variant: variantId, surface, audience });
}
